import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * Toasts (egdl-toast, --error, --success). Bottom-right, auto-dismiss after 6 s.
 * useToast()(text, kind) where kind is 'info' | 'error' | 'success'.
 */
const ToastContext = createContext(() => {});

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timers = useRef({});

    const dismiss = useCallback((id) => {
        clearTimeout(timers.current[id]);
        delete timers.current[id];
        setToasts((t) => t.filter((x) => x.id !== id));
    }, []);

    const push = useCallback((text, kind = 'info', ms = 6000) => {
        const id = Math.random().toString(36).slice(2);
        setToasts((t) => [...t.slice(-3), { id, text, kind }]);
        timers.current[id] = setTimeout(() => dismiss(id), ms);
        return id;
    }, [dismiss]);

    const value = useMemo(() => push, [push]);
    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="eg-toasts" aria-live="assertive">
                {toasts.map((t) => (
                    <div key={t.id} role="status" className={`eg-toast eg-toast--${t.kind}`}>
                        <span>{t.text}</span>
                        <button type="button" className="eg-toast__close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    return useContext(ToastContext);
}
